import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit, updateDoc } from 'firebase/firestore';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import './CustomerDetail.css';

interface Customer {
  uid: string;
  email: string;
  name: string;
  phoneNumber?: string;
  phone?: string;
  role: string;
  status?: 'active' | 'inactive' | 'banned' | 'pending_approval';
  createdAt: any;
  updatedAt?: any;
  profilePhotoUrl?: string;
  avatar?: string;
  totalBookings?: number;
  lastLoginAt?: any;
  address?: string;
  bio?: string;
  savedAddresses?: Array<{
    id?: string;
    label?: string;
    address: string;
    isDefault?: boolean;
  } | string>;
}

interface Booking {
  id: string;
  userInfo: {
    name: string;
    email: string;
  };
  serviceItem: {
    name: string;
    price: number;
  };
  finalPrice?: number;
  status: string;
  createdAt: any;
  providerId?: string;
  subcategoryId?: string;
}

interface BookingWithProvider extends Booking {
  providerName?: string;
  subcategoryName?: string;
}

const CustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [recentBookings, setRecentBookings] = useState<BookingWithProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0,
    totalSpent: 0
  });

  // Inline edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedStatus, setEditedStatus] = useState<'active' | 'inactive' | 'banned' | 'pending_approval'>('active');

  // Password change states
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Reset editing states when customer changes
  useEffect(() => {
    if (customer) {
      setEditedName(customer.name || '');
      setEditedEmail(customer.email || '');
      setEditedPhone(customer.phoneNumber || customer.phone || '');
      setEditedStatus(customer.status || 'active');
    }
    setIsEditingName(false);
    setIsEditingEmail(false);
    setIsEditingPhone(false);
    setIsEditingStatus(false);
  }, [customer]);

  useEffect(() => {
    if (customerId) {
      fetchCustomerDetails();
    }
  }, [customerId]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải thông tin khách hàng:', customerId);

      // Fetch customer information
      const customerDoc = await getDoc(doc(db, 'users', customerId!));
      if (!customerDoc.exists()) {
        setError('Không tìm thấy khách hàng');
        return;
      }

      const customerData = {
        ...customerDoc.data(),
        uid: customerDoc.id
      } as Customer;
      console.log('Customer data loaded:', customerData);
      setCustomer(customerData);

      // Fetch recent bookings
      console.log('Fetching bookings...');
      let bookingsData: BookingWithProvider[] = [];
      
      try {
        // Try query with orderBy first
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('userId', '==', customerId),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        bookingsData = bookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as BookingWithProvider));
      } catch (bookingsError) {
        console.warn('Bookings query with orderBy failed, trying simple query:', bookingsError);
        
        // Fallback to simple query
        const simpleBookingsQuery = query(
          collection(db, 'bookings'),
          where('userId', '==', customerId),
          limit(10)
        );
        const simpleBookingsSnapshot = await getDocs(simpleBookingsQuery);
        bookingsData = simpleBookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as BookingWithProvider));
        
        // Sort manually by createdAt
        bookingsData.sort((a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          
          const aTime = a.createdAt?.seconds ? a.createdAt.seconds : 0;
          const bTime = b.createdAt?.seconds ? b.createdAt.seconds : 0;
          return bTime - aTime;
        });
      }

      // Enrich bookings with provider and subcategory names
      for (let booking of bookingsData) {
        // Get provider name
        if (booking.providerId) {
          try {
            const providerDoc = await getDoc(doc(db, 'users', booking.providerId));
            if (providerDoc.exists()) {
              booking.providerName = providerDoc.data().name || 'Không có tên';
            }
          } catch (error) {
            console.error('Error fetching provider:', error);
          }
        }

        // Get subcategory name
        if (booking.subcategoryId) {
          try {
            const subcategoryDoc = await getDoc(doc(db, 'subcategories', booking.subcategoryId));
            if (subcategoryDoc.exists()) {
              booking.subcategoryName = subcategoryDoc.data().name || 'Không có tên';
            }
          } catch (error) {
            console.error('Error fetching subcategory:', error);
          }
        }
      }

      setRecentBookings(bookingsData);

      // Calculate stats
      const totalBookings = bookingsData.length;
      const completedBookings = bookingsData.filter(b => b.status === 'completed').length;
      const pendingBookings = bookingsData.filter(b => ['pending', 'confirmed'].includes(b.status)).length;
      const totalSpent = bookingsData
        .filter(b => b.status === 'completed')
        .reduce((sum, b) => sum + (b.finalPrice || b.serviceItem?.price || 0), 0);

      setStats({
        totalBookings,
        completedBookings,
        pendingBookings,
        totalSpent
      });

    } catch (error) {
      console.error('Lỗi khi tải thông tin khách hàng:', error);
      setError('Không thể tải thông tin khách hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (field: 'name' | 'email' | 'phone' | 'status') => {
    if (!customer || !customerId) return;

    try {
      setUpdating(true);
      const updates: { [key: string]: any } = {};

      switch (field) {
        case 'name':
          if (editedName.trim() === '') {
            alert('Tên không được để trống');
            return;
          }
          updates.name = editedName.trim();
          break;
        case 'email':
          if (editedEmail.trim() === '') {
            alert('Email không được để trống');
            return;
          }
          if (!editedEmail.includes('@')) {
            alert('Email không hợp lệ');
            return;
          }
          updates.email = editedEmail.trim();
          break;
        case 'phone':
          if (editedPhone.trim() === '') {
            alert('Số điện thoại không được để trống');
            return;
          }
          updates.phoneNumber = editedPhone.trim();
          break;
        case 'status':
          updates.status = editedStatus;
          break;
      }

      updates.updatedAt = new Date();
      await updateDoc(doc(db, 'users', customerId), updates);

      // Update local state
      setCustomer(prev => prev ? { ...prev, ...updates } : null);

      // Reset edit mode
      switch (field) {
        case 'name':
          setIsEditingName(false);
          break;
        case 'email':
          setIsEditingEmail(false);
          break;
        case 'phone':
          setIsEditingPhone(false);
          break;
        case 'status':
          setIsEditingStatus(false);
          break;
      }

    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Có lỗi khi cập nhật thông tin. Vui lòng thử lại.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = (field: 'name' | 'email' | 'phone' | 'status') => {
    switch (field) {
      case 'name':
        setEditedName(customer?.name || '');
        setIsEditingName(false);
        break;
      case 'email':
        setEditedEmail(customer?.email || '');
        setIsEditingEmail(false);
        break;
      case 'phone':
        setEditedPhone(customer?.phoneNumber || customer?.phone || '');
        setIsEditingPhone(false);
        break;
      case 'status':
        setEditedStatus(customer?.status || 'active');
        setIsEditingStatus(false);
        break;
    }
  };

  const handleChangePassword = async () => {
    if (!customer?.uid) {
      alert('Không xác định được UID của khách hàng');
      return;
    }

    if (newPassword.length < 6) {
      alert('Mật khẩu mới phải có ít nhất 6 ký tự');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      alert('Mật khẩu mới và xác nhận mật khẩu không khớp');
      return;
    }

    try {
      setChangingPassword(true);
      const callable = httpsCallable(functions, 'setUserPassword');
      await callable({ uid: customer.uid, newPassword, expectedRole: 'customer' });
      setNewPassword('');
      setConfirmNewPassword('');
      setIsEditingPassword(false);
      alert('Đã cập nhật mật khẩu mới thành công');
    } catch (err: any) {
      const msg = err?.message || 'Có lỗi khi thay đổi mật khẩu';
      alert(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const updateCustomerStatus = async (newStatus: string) => {
    if (!customer) return;

    try {
      setUpdating(true);
      const customerRef = doc(db, 'users', customer.uid);
      await updateDoc(customerRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      setCustomer(prev => prev ? { ...prev, status: newStatus as any } : null);
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      setError('Cập nhật trạng thái thất bại');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Chưa cập nhật';
    
    try {
      let date: Date;
      
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Lỗi định dạng';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0 VND';
    return amount.toLocaleString('vi-VN') + ' VND';
  };

  const getStatusLabel = (status?: string) => {
    const statusMap: { [key: string]: string } = {
      'active': 'Hoạt động',
      'inactive': 'Tạm dừng',
      'banned': 'Bị cấm',
      'pending_approval': 'Đang chờ phê duyệt'
    };
    return status ? statusMap[status] || status : 'Hoạt động';
  };

  const getStatusClass = (status?: string) => {
    const statusClassMap: { [key: string]: string } = {
      'active': 'status-active',
      'inactive': 'status-inactive',
      'banned': 'status-banned',
      'pending_approval': 'status-pending-approval'
    };
    return status ? statusClassMap[status] || '' : 'status-active';
  };

  const getBookingStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Chờ xử lý',
      'confirmed': 'Đã xác nhận',
      'cancelled': 'Đã hủy',
      'completed': 'Hoàn thành',
      'pending_completion': 'Chờ hoàn thành'
    };
    return statusMap[status] || status;
  };

  const getBookingStatusClass = (status: string) => {
    const statusClassMap: { [key: string]: string } = {
      'pending': 'booking-status-pending',
      'confirmed': 'booking-status-confirmed',
      'cancelled': 'booking-status-cancelled',
      'completed': 'booking-status-completed',
      'pending_completion': 'booking-status-pending-completion'
    };
    return statusClassMap[status] || '';
  };

  const getPhoneNumber = (customer: Customer) => {
    return customer.phoneNumber || customer.phone || 'Chưa cập nhật';
  };

  if (loading) {
    return (
      <div className="customer-detail">
        <div className="page-header">
          <h2>Chi tiết khách hàng</h2>
        </div>
        <div className="loading">
          <div className="loading-spinner">Đang tải thông tin...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-detail">
        <div className="page-header">
          <h2>Chi tiết khách hàng</h2>
        </div>
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/customers')} className="back-button">
          Quay lại danh sách
        </button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="customer-detail">
        <div className="page-header">
          <h2>Chi tiết khách hàng</h2>
        </div>
        <div className="no-data">Không tìm thấy thông tin khách hàng</div>
        <button onClick={() => navigate('/customers')} className="back-button">
          Quay lại danh sách
        </button>
      </div>
    );
  }

  return (
    <div className="customer-detail">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Quay lại
        </button>
        <div className="header-content">
          <div className="customer-info">
            {(customer.profilePhotoUrl || customer.avatar) ? (
              <img 
                src={customer.profilePhotoUrl || customer.avatar} 
                alt="Avatar khách hàng" 
                className="customer-photo"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIiBmaWxsPSIjRTBFMEUwIi8+CjxwYXRoIGQ9Ik01MCAzNUM0NC40NzcyIDM1IDQwIDM5LjQ3NzIgNDAgNDVDNDAgNTAuNTIyOCA0NC40NzcyIDU1IDUwIDU1QzU1LjUyMjggNTUgNjAgNTAuNTIyOCA2MCA0NUM2MCAzOS40NzcyIDU1LjUyMjggMzUgNTAgMzVaTTMwIDc1VjcwQzMwIDYyLjI2ODYgMzYuMjY4NiA1NiA0NCA1Nkg1NkM2My43MzE0IDU2IDcwIDYyLjI2ODYgNzAgNzBWNzVIMzBaIiBmaWxsPSIjOTA5MDkwIi8+Cjwvc3ZnPgo=';
                }}
              />
            ) : (
              <div className="customer-photo default-avatar">
                {customer.name ? customer.name.charAt(0).toUpperCase() : '?'}
              </div>
            )}
            <div className="customer-details">
              <h2>{customer.name}</h2>
              <p className="customer-email">{customer.email}</p>
              <span className={`status ${getStatusClass(customer.status)}`}>
                {getStatusLabel(customer.status)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-content">
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-card">
            <h3>Tổng booking</h3>
            <p className="stat-number">{stats.totalBookings}</p>
          </div>
          <div className="stat-card">
            <h3>Đã hoàn thành</h3>
            <p className="stat-number">{stats.completedBookings}</p>
          </div>
          <div className="stat-card">
            <h3>Đang xử lý</h3>
            <p className="stat-number">{stats.pendingBookings}</p>
          </div>
          <div className="stat-card">
            <h3>Tổng chi tiêu</h3>
            <p className="stat-number">{formatCurrency(stats.totalSpent)}</p>
          </div>
        </div>

        {/* Customer Information */}
        <div className="info-section">
          <h3>Thông tin cá nhân</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Tên đầy đủ:</label>
              {isEditingName ? (
                <div className="edit-controls">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="edit-input"
                    disabled={updating}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('name')}
                      disabled={updating || editedName.trim() === ''}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('name')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">{customer.name || 'Chưa cập nhật'}</span>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="edit-btn"
                    title="Chỉnh sửa tên"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
            <div className="info-item">
              <label>Email:</label>
              {isEditingEmail ? (
                <div className="edit-controls">
                  <input
                    type="email"
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    className="edit-input"
                    disabled={updating}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('email')}
                      disabled={updating || !editedEmail.includes('@')}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('email')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">{customer.email || 'Chưa cập nhật'}</span>
                  <button
                    onClick={() => setIsEditingEmail(true)}
                    className="edit-btn"
                    title="Chỉnh sửa email"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
            <div className="info-item">
              <label>Số điện thoại:</label>
              {isEditingPhone ? (
                <div className="edit-controls">
                  <input
                    type="tel"
                    value={editedPhone}
                    onChange={(e) => setEditedPhone(e.target.value)}
                    className="edit-input"
                    disabled={updating}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('phone')}
                      disabled={updating || editedPhone.trim() === ''}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('phone')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">{customer.phoneNumber || customer.phone || 'Chưa cập nhật'}</span>
                  <button
                    onClick={() => setIsEditingPhone(true)}
                    className="edit-btn"
                    title="Chỉnh sửa số điện thoại"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
            <div className="info-item">
              <label>Trạng thái:</label>
              {isEditingStatus ? (
                <div className="edit-controls">
                  <select
                    value={editedStatus}
                    onChange={(e) => setEditedStatus(e.target.value as 'active' | 'inactive' | 'banned' | 'pending_approval')}
                    className="edit-select"
                    disabled={updating}
                  >
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Tạm dừng</option>
                    <option value="banned">Bị cấm</option>
                    <option value="pending_approval">Đang chờ phê duyệt</option>
                  </select>
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('status')}
                      disabled={updating}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('status')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className={`status-badge ${getStatusClass(customer.status)}`}>
                    {getStatusLabel(customer.status)}
                  </span>
                  <button
                    onClick={() => setIsEditingStatus(true)}
                    className="edit-btn"
                    title="Chỉnh sửa trạng thái"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
            <div className="info-item">
              <label>Mật khẩu:</label>
              {isEditingPassword ? (
                <div className="edit-controls">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="edit-input"
                    disabled={changingPassword}
                    placeholder="Mật khẩu mới"
                  />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="edit-input"
                    disabled={changingPassword}
                    placeholder="Xác nhận mật khẩu mới"
                    style={{ marginTop: '8px' }}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={handleChangePassword}
                      disabled={changingPassword || newPassword.length < 6 || newPassword !== confirmNewPassword}
                      className="save-btn"
                    >
                      {changingPassword ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => { setIsEditingPassword(false); setNewPassword(''); setConfirmNewPassword(''); }}
                      disabled={changingPassword}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">••••••••</span>
                  <button
                    onClick={() => setIsEditingPassword(true)}
                    className="edit-btn"
                    title="Đổi mật khẩu"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>
            <div className="info-item">
              <label>Ngày đăng ký:</label>
              <span>{formatDate(customer.createdAt)}</span>
            </div>
            <div className="info-item">
              <label>Cập nhật cuối:</label>
              <span>{formatDate(customer.updatedAt)}</span>
            </div>
            <div className="info-item">
              <label>Đăng nhập lần cuối:</label>
              <span>{formatDate(customer.lastLoginAt)}</span>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bookings-section">
          <h3>Booking gần đây</h3>
          {recentBookings.length === 0 ? (
            <div className="no-bookings">
              <p>Khách hàng chưa có booking nào</p>
            </div>
          ) : (
            <div className="bookings-table-container">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Dịch vụ</th>
                    <th>Nhà cung cấp</th>
                    <th>Danh mục</th>
                    <th>Giá</th>
                    <th>Trạng thái</th>
                    <th>Ngày tạo</th>
                    <th>Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.serviceItem.name}</td>
                      <td>{booking.providerName || 'Chưa có'}</td>
                      <td>{booking.subcategoryName || 'N/A'}</td>
                      <td className="price-cell">{formatCurrency(booking.finalPrice || booking.serviceItem.price)}</td>
                      <td>
                        <span className={`booking-status ${getBookingStatusClass(booking.status)}`}>
                          {getBookingStatusLabel(booking.status)}
                        </span>
                      </td>
                      <td>{formatDate(booking.createdAt)}</td>
                      <td>
                        <button 
                          className="view-booking-btn"
                          onClick={() => navigate(`/bookings/${booking.id}`)}
                        >
                          Xem chi tiết
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail; 